"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StateFinder from "@/utils/stateFinder";
import axios from "axios";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  MapPin,
  Plus,
  X,
} from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UseFormReturn } from "react-hook-form";
import z from "zod";
import { BootcampSchema } from "../Bootcamp/Bootcampform";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";

interface Address {
  id: string;
  pincode: string;
  addressLine1: string;
  city: string;
  addressLine2?: string;
  landMark?: string;
  state?: string;
  country?: string;
  lastUsed: boolean;
  phone: string;
}

const formatAddress = (address: Address) => {
  const parts = [address.city, address.pincode].filter(Boolean);
  return parts.join(", ");
};

const formatLastUsed = (isLastUsed: boolean) => {
  return isLastUsed ? "Last Used" : null;
};

export function AddressSelector({
  phoneNumber,
  isSelectAddressEnabled,
  selectedId,
  setSelectedId,
  form,
  stateFinder,
  isLoading,
  country,
  setCountry,
  setSelectedState,
  address,
}: {
  phoneNumber: any;
  isSelectAddressEnabled: any;
  selectedId: string | null;
  setSelectedId: Dispatch<SetStateAction<string | null>>;
  form: UseFormReturn<z.infer<typeof BootcampSchema>>;
  stateFinder: StateFinder;
  isLoading: boolean;
  country: any;
  setCountry: any;
  setSelectedState: any;
  address?: Omit<Address, "lastUsed" | "phone" | "id">;
}) {
  const [isSearching, setIsSearching] = useState(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [moreAddressesPage, setMoreAddressesPage] = useState(0);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const ITEMS_PER_PAGE = 2;

  const selectInitialAddress = useCallback(
    (list: Address[]) => {
      const preferred =
        list.find((addr) => addr.lastUsed)?.id ?? list[0]?.id ?? "";
      if (!preferred) return;
      if (!selectedId || !list.some((addr) => addr.id === selectedId)) {
        setSelectedId(preferred);
      }
    },
    [selectedId, setSelectedId]
  );

  const handleAddNew = useCallback(async () => {
    if (
      address &&
      (!address.addressLine1 ||
        !address.city ||
        !address.country ||
        !address.pincode)
    )
      return;

    const addressPayload: Address = {
      ...(address as Address),
      phone: phoneNumber,
      lastUsed: true,
    };

    try {
      const { data: addedAddress } = await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_API}address/create/user/${phoneNumber}`,
        addressPayload
      );

      setAddresses((prev) => [addedAddress, ...prev]);
      setSelectedId(addedAddress.id);
      setIsAddingNew(false);
    } catch (error) {
      console.error("error while adding address: ", error);
    }
  }, [address, phoneNumber, setSelectedId]);

  const selectedAddress = useMemo(
    () => addresses.find((a) => a.id === selectedId),
    [addresses, selectedId]
  );

  const otherAddresses = useMemo(
    () => addresses.filter((a) => a.id !== selectedId),
    [addresses, selectedId]
  );

  const startIndex = useMemo(
    () => moreAddressesPage * ITEMS_PER_PAGE,
    [moreAddressesPage]
  );

  const paginatedMoreAddresses = useMemo(
    () => otherAddresses.slice(startIndex, startIndex + ITEMS_PER_PAGE),
    [otherAddresses, startIndex]
  );

  const totalMorePages = useMemo(
    () => Math.ceil(otherAddresses.length / ITEMS_PER_PAGE),
    [otherAddresses.length]
  );

  useEffect(() => {
    if (isSelectAddressEnabled && phoneNumber) {
      setIsSearching(true);
      axios
        .get(
          `${process.env.NEXT_PUBLIC_BASE_API}address/user/phone/${phoneNumber}`
        )
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setAddresses(res.data);
            selectInitialAddress(res.data);
          } else {
            setAddresses([]);
            selectInitialAddress([]);
          }
        })
        .catch((error) => {
          console.log("error while fetching user specific address: ", error);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  }, [isSelectAddressEnabled, phoneNumber, selectInitialAddress]);

  return (
    <div className="w-full space-y-3">
      <div className="space-y-2">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex gap-2">
            <h3 className="text-sm font-normal">Your Address Info</h3>
            {addresses.length > 0 && (
              <Badge
                variant="secondary"
                className="rounded-full bg-primary/10 text-primary border-none text-xs"
              >
                {addresses.length}
              </Badge>
            )}
          </div>
          {!isAddingNew && addresses.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddingNew(true)}
              className="w-fit rounded-lg border-secondary-foreground/10 text-xs h-8"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Address
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {/* Selected address - compact */}
        {!isAddingNew && selectedAddress && (
          <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="group relative flex items-start gap-3 p-3 rounded-lg border-2 border-[#172554] bg-primary/5 ring-1 ring-primary/20">
              <div className="mt-0.5 p-1.5 rounded-lg bg-[#172554] text-primary-foreground">
                <MapPin className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 space-y-0.5 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm truncate">
                    {selectedAddress.addressLine1}
                  </span>
                  {formatLastUsed(selectedAddress.lastUsed) != null && (
                    <Badge
                      variant="outline"
                      className={`text-[9px] uppercase tracking-wider font-bold h-4 px-1 border-primary/20 ${
                        selectedAddress.lastUsed
                          ? "bg-[#172554] text-white"
                          : "bg-white   text-[#172554] "
                      }`}
                    >
                      {formatLastUsed(selectedAddress.lastUsed)}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground space-y-0">
                  {selectedAddress.addressLine2 && (
                    <p>{selectedAddress.addressLine2}</p>
                  )}
                  {selectedAddress.landMark && (
                    <p>{selectedAddress.landMark}</p>
                  )}
                  <p>{formatAddress(selectedAddress)}</p>
                </div>
              </div>
              <div className="h-5 w-5 rounded-full border-2 border-[#172554] bg-[#172554] flex items-center justify-center flex-shrink-0">
                <div className="h-1.5 w-1.5 rounded-full bg-white" />
              </div>
            </div>

            {/* See more button */}
            {otherAddresses.length > 0 && (
              <Button
                variant="outline"
                type="button"
                size="sm"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                  setMoreAddressesPage(0);
                }}
                className="w-full rounded-lg border-secondary-foreground/10 text-xs h-8"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5 mr-1" />
                    See Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5 mr-1" />
                    See More ({otherAddresses.length})
                  </>
                )}
              </Button>
            )}

            {/* Expanded addresses with pagination */}
            {isExpanded && otherAddresses.length > 0 && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
                <div className="grid gap-2">
                  {paginatedMoreAddresses.map((address) => (
                    <div
                      key={address.id}
                      onClick={() => {
                        setSelectedId(address.id);
                        setIsExpanded(false);
                      }}
                      className="group relative flex items-start gap-3 p-3 rounded-lg border-2 border-secondary/50 cursor-pointer transition-all"
                    >
                      <div className="mt-0.5 p-1.5 rounded-lg bg-secondary text-muted-foreground  transition-colors">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-0.5 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm truncate">
                            {address.addressLine1}
                          </span>
                          {formatLastUsed(address.lastUsed) && (
                            <Badge
                              variant="outline"
                              className={`text-[9px] uppercase tracking-wider font-bold h-4 px-1 border-primary/20  ${
                                address.lastUsed
                                  ? "bg-[#172554] text-white"
                                  : "bg-white   text-[#172554] "
                              }`}
                            >
                              {formatLastUsed(address.lastUsed)}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground space-y-0">
                          {address.addressLine2 && (
                            <p>{address.addressLine2}</p>
                          )}
                          {address.landMark && <p>{address.landMark}</p>}
                          <p>{formatAddress(address)}</p>
                        </div>
                      </div>
                      <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30  transition-colors flex-shrink-0" />
                    </div>
                  ))}
                </div>

                {/* Pagination arrows */}
                {totalMorePages > 1 && (
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="outline"
                      type="button"
                      size="icon"
                      onClick={() =>
                        setMoreAddressesPage((p) => Math.max(0, p - 1))
                      }
                      disabled={moreAddressesPage === 0}
                      className="rounded-md h-7 w-7 px-0"
                    >
                      <ChevronLeft className="h-6 w-6 text-[#172554]" />
                    </Button>
                    <span className="text-xs text-muted-foreground font-medium px-2">
                      {moreAddressesPage + 1} of {totalMorePages}
                    </span>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() =>
                        setMoreAddressesPage((p) =>
                          Math.min(totalMorePages - 1, p + 1)
                        )
                      }
                      disabled={moreAddressesPage === totalMorePages - 1}
                      className="rounded-md w-7 h-7 px-0"
                    >
                      <ChevronRight className="w-6 h-6 text-[#172554]" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Add new address section */}
        {isAddingNew && (
          <div className="p-3 rounded-lg border-2 border-[#17255420] bg-[#1725545] space-y-2 animate-in zoom-in-95 duration-300">
            <div className="space-y-2">
              <>
                <FormField
                  name="address.addressLine1"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="normal-case">
                        Address Line 1
                      </FormLabel>
                      <FormControl className="h-8 text-sm bg-background border-secondary/50 rounded !my-0">
                        <Input
                          {...field}
                          required
                          placeholder="Enter your city"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="address.addressLine2"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="normal-case">
                        Address Line 2
                      </FormLabel>
                      <FormControl className="h-8 text-sm bg-background border-secondary/50 rounded !my-0">
                        <Input
                          {...field}
                          required
                          placeholder="Enter your city"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="address.landMark"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="normal-case">LandMark</FormLabel>
                      <FormControl className="h-8 text-sm bg-background border-secondary/50 rounded !my-0">
                        <Input
                          {...field}
                          required
                          placeholder="Enter your city"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    name="address.pincode"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="normal-case">Pincode</FormLabel>
                        <FormControl className="h-8 text-sm bg-background border-secondary/50 rounded !my-0">
                          <Input
                            {...field}
                            type="text"
                            inputMode="numeric"
                            required
                            placeholder="Enter your pincode"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="address.city"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="normal-case">City</FormLabel>
                        <FormControl className="h-8 text-sm bg-background border-secondary/50 rounded !my-0">
                          <Input
                            {...field}
                            required
                            placeholder="Enter your city"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleAddNew}
                size="sm"
                type="button"
                className="flex-1 text-xs h-8 rounded"
                disabled={
                  address &&
                  (!address.addressLine1 ||
                    !address.city ||
                    !address.country ||
                    !address.pincode)
                }
              >
                Save
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddingNew(false);
                }}
                size="sm"
                className="text-xs h-8 rounded"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isAddingNew &&
          addresses.length === 0 &&
          !isSearching &&
          phoneNumber && (
            <div className="py-4 text-center border-2 border-dashed border-secondary rounded-lg animate-in fade-in duration-500">
              <MapPin className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-xs font-medium mb-2">
                No addresses found
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddingNew(true)}
                className="rounded-lg text-xs h-8"
                size="sm"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Address
              </Button>
            </div>
          )}
      </div>
    </div>
  );
}
